import Immutable from 'immutable';
import React from 'react';

import FormItem from '../../forms/formitem';
import TextInput from '../../forms/textinput';
import CommentInput from '../../forms/commentinput';
import EntityLink from '../../forms/entitylink';
import Button from '../../forms/button';
import LookupInput from '../../forms/lookupinput';
import LookupListInput from '../../forms/lookuplistinput';
import DateInput from '../../forms/dateinput';
import SearchDateInput from '../../forms/searchdateinput';
import SearchNumberInput from '../../forms/searchnumberinput';
import SearchRuntimeInput from '../../forms/searchruntimeinput';
import NumberInput from '../../forms/numberinput';
import RuntimeInput from '../../forms/runtimeinput';

import StaticLookup from '../../../data/staticlookup';
import {ErrorList, isEmpty} from '../../../data/validation';
import { getPhrase } from '../../../data/phrases';

const EMPTY_LOOKUP = StaticLookup(Immutable.List([]));

export default function FormItemDefinition(name, type) {
	if (!(this instanceof FormItemDefinition)) {
		return new FormItemDefinition(name, type);
	}
	const me = this;
	let path = name;
	let isRootPath = false;
	let lookup;
	let lookupCallback;
	let indexLookup;
	let indexLookupCallback;
	let textAlign;
	let width;
	let fixedWidth;
	let minWidth;
	let multiline;
	let inputMinHeight;
	let validator;
	let isVisibleCallback;
	let isHidden;
	let withTime = false;
	let mandatory;
	let margin;
	let isMandatoryCallback;
	let readOnly;
	let isReadOnlyCallback;
	let maxLength;
	let isCombo;
	let inputOnly;
	let fixedParams;
	let fixedParamsCallback;
	let timeout;
	let processForSearch;
	let label;
	let noError;
	let maxDecimals;
	let inputFilter;
	let entityName;
	let changeListeners = [];
	let hilited;
	let hilitedCallback;
	let offline;
	let sortNullsLast;
	let filterType;
	let presets;
	let buttonText;
	let buttonListener;
	let buttonIconName;
	let disabled;
	let disabledCallback;
	let leftIcons;
	let leftIconsCallback;
	let rightIcons;
	let rightIconsCallback;
	let customInput;
	let className;
	let classNameCallback;

	let placeholder;
	let placeholderCallback;

	let customLookupTextCallback;

	let noInput;
	let noInputCallback;

	let renderInput = null;

	const isContextReadOnly = function(ctx) {
		if (isRootPath) {
			return ctx.isRootReadOnly(path);
		} else {
			return ctx.isReadOnly(path);
		}
	};

	this.setMargin = function(value) {
		margin = value;
		return this;
	};

	this.setInputOnly = function(value) {
		inputOnly = value;
		return this;
	};

	this.setPlaceholder = function(value) {
		if (typeof value === 'function') {
			placeholderCallback = value;
			placeholder = null;
		} else {
			placeholder = value;
			placeholderCallback = null;
		}
		return this;
	};

	const getPlaceholder = function(ctx) {
		if (placeholderCallback) {
			return placeholderCallback(ctx);
		} else {
			return placeholder;
		}
	}

	this.setCustomInput = function(value) {
		customInput = value;
		renderInput = renderCustomInput;
		filterType = null;
		return this;
	};

	this.setCustomLookupText = function(value) {
		customLookupTextCallback = value;
		return this;
	};

	this.setTextAlign = function(value) {
		textAlign = value;
		return this;
	};

	this.setNoInput = function(value) {
		if (typeof value === 'function') {
			noInput = null;
			noInputCallback = value;
		} else {
			noInputCallback = null;
			noInput = value;
		}
		return this;
	};

	this.setClassName = function(value) {
		if (typeof value === 'function') {
			className = null;
			classNameCallback = value;
		} else {
			className = value;
			classNameCallback = null;
		}
		return this;
	};

	this.setWithTime = function(value) {
		withTime = value;
		return this;
	};

	const getClassName = function(ctx) {
		if (classNameCallback) {
			return classNameCallback(ctx);
		} else {
			return className;
		}
	}

	const getNoInput = function(ctx) {
		if (noInputCallback) {
			return noInputCallback(ctx);
		} else {
			return noInput;
		}
	};

	this.setLeftIcons = function(value) {
		if (typeof value === 'function') {
			leftIconsCallback = value;
			leftIcons = null;
		} else {
			leftIconsCallback = null;
			leftIcons = value;
		}
		return this;
	};

	this.setRightIcons = function(value) {
		if (typeof value === 'function') {
			rightIconsCallback = value;
			rightIcons = null;
		} else {
			rightIconsCallback = null;
			rightIcons = value;
		}
		return this;
	};

	const getLeftIcons = function(ctx) {
		if (leftIconsCallback) {
			return leftIconsCallback(ctx);
		} else {
			return leftIcons;
		}
	}

	const getRightIcons = function(ctx) {
		if (rightIconsCallback) {
			return rightIconsCallback(ctx);
		} else {
			return rightIcons;
		}
	}

	this.setDisabled = function(value) {
		if (typeof value === 'function') {
			disabledCallback = value;
			disabled = false;
		} else {
			disabled = value;
			disabledCallback = null;
		}
		return this;
	};

	const isDisabled = function(ctx) {
		if (disabledCallback) {
			return disabledCallback(ctx);
		} else {
			return disabled;
		}
	};

	this.setButtonText = function(value) {
		buttonText = value;
		return this;
	};

	this.setButtonIconName = function(value) {
		buttonIconName = value;
		return this;
	};

	this.setButtonListener = function(value) {
		buttonListener = value;
		return this;
	}

	this.setPresets = function(value) {
		presets = value;
		return this;
	};

	this.getFilterType = function() {
		return filterType;
	};

	this.setFilterType = function(value) {
		filterType = value;
		return this;
	};

	this.setSortNullsLast = function(value) {
		sortNullsLast = value;
		return this;
	};

	this.getSortNullsLast = function() {
		return sortNullsLast;
	};

	this.getPath = function() {
		return path;
	};

	this.setOffline = function(value) {
		offline = value;
		return this;
	};

	this.getType = function() {
		return type;
	};

	const isReadOnly = function(ctx) {
		let res = readOnly || isContextReadOnly(ctx) || (isReadOnlyCallback && isReadOnlyCallback(ctx, name));
		return res;
	};

	this.isReadOnly = function(ctx) {
		return isReadOnly(ctx);
	};

	this.setEntityName = function(value) {
		entityName = value;
		return this;
	};

	const isMandatory = function(ctx) {
		if (isMandatoryCallback) {
			return isMandatoryCallback(ctx, name);
		} else {
			return mandatory;
		}
	};

	this.offChange = function(value) {
		changeListeners = changeListeners.filter(e => e !== value);
		return this;
	};

	this.onChange = function(value) {
		if (changeListeners.indexOf(value) < 0) {
			changeListeners.push(value);
		}
		return this;
	};

	this.setMandatory = function(value) {
		if (typeof value === 'function') {
			isMandatoryCallback = value;
			mandatory = false;
		} else {
			isMandatoryCallback = null;
			mandatory = value;
		}
		return this;
	};

	this.setHilited = function(value) {
		if (typeof value === 'function') {
			hilitedCallback = value;
			hilited = false;
		} else {
			hilitedCallback = null;
			hilited = value;
		}
		return this;
	};

	this.isHilited = function(ctx) {
		if (hilitedCallback) {
			return hilitedCallback(ctx);
		} else {
			return hilited;
		}
	};

	this.getName = function() {
		return name;
	};

	this.setTimeout = function(value) {
		timeout = value;
		return this;
	};

	this.setInputFilter = function(value) {
		inputFilter = value;
		return this;
	};

	this.setMaxDecimals = function(value) {
		maxDecimals = value;
		return this;
	};

	this.setMinWidth = function(value) {
		minWidth = value;
		return this;
	}

	this.setNoError = function(value) {
		noError = value;
		return this;
	};

	this.getNoError = function() {
		return noError;
	};

	this.hasLabel = function() {
		return label !== null && label !== undefined;
	}

	this.setLabel = function(value) {
		label = value;
		return this;
	};

	const renderUnknown = function(ctx) {
		if (type) {
			return (
				<div>Unknown input type - {type}</div>
			);
		} else {
			return (
				<div>Input type not specified</div>
			);
		}
	};

	const getValue = function(ctx) {
		return isRootPath ? ctx.getRootProperty(path) : ctx.getProperty(path);
	};

	this.isRootPath = function() {
		return isRootPath;
	}

	this.getValue = function(ctx) {
		return getValue(ctx);
	};

	this.getError = function(ctx) {
		return isRootPath ? ctx.getRootError(path) : ctx.getError(path);
	};

	const setValue = function(ctx, value) {
		if (offline) {
			changeListeners.forEach(listener => {
				listener(ctx, me, value);
			});
		} else {
			if (isRootPath) {
				ctx.setRootProperty(path, value);
			} else {
				ctx.setProperty(path, value);
			}
			changeListeners.forEach(listener => {
				listener(ctx, me, value);
			});
			const errors = me.validateOnChange(ctx);
			ctx.setRootErrors(errors.mergeWith(ctx.getRootErrors()));
		}
	};

	this.setFixedParams = function(value) {
		if (typeof value === 'function') {
			fixedParams = null;
			fixedParamsCallback = value;
		} else {
			fixedParams = value;
			fixedParamsCallback = null;
		}
		return this;
	};

	const renderCustomInput = function(ctx) {
		return customInput({
			value: getValue(ctx),
			onChange: value => setValue(ctx, value),
			readOnly: isReadOnly(ctx),
			ctx
		});
	};

	const renderTextInput = function(ctx) {
		return <TextInput
			changeOnBlur={true}
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			multiline={multiline}
			minHeight={inputMinHeight}
			inputFilter={inputFilter}
			maxLength={maxLength}
			textAlign={textAlign}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderPasswordInput = function(ctx) {
		return <TextInput
			changeOnBlur={true}
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			multiline={multiline}
			inputFilter={inputFilter}
			password
		/>;
	};

	const renderCommentInput = function(ctx) {
		return <CommentInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
		/>;
	};

	const renderEntityLink = function(ctx) {
		return <EntityLink
			entityId={getValue(ctx)}
			entityName={entityName}
		/>;
	};

	const renderButton = function(ctx) {
		return <Button
			text={buttonText || label}
			onAction={() => {
				if (buttonListener) {
					buttonListener({
						name,
						ctx
					});
				}
				ctx.trigger('action', {
					source: 'button',
					name
				});
			}}
			disabled={isDisabled(ctx)}
			iconName={buttonIconName}
			type="light"
		/>;
	};

	const renderRuntimeInput = function(ctx) {
		return <RuntimeInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
		/>;
	};

	const renderNumberInput = function(ctx) {
		return <NumberInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			maxDecimals={maxDecimals}
		/>;
	};

	const getFixedParams = function(ctx) {
		if (fixedParamsCallback) {
			return fixedParamsCallback(ctx);
		} else {
			return fixedParams;
		}
	};

	const getLookup = function(ctx) {
		if (lookupCallback) {
			return lookupCallback(ctx) || EMPTY_LOOKUP;
		}
		if (typeof lookup === 'string') {
			return ctx.getLookup(lookup, getFixedParams(ctx)) || EMPTY_LOOKUP;
		}
		return lookup || EMPTY_LOOKUP;
	}

	this.getLookup = function(ctx) {
		return getLookup(ctx);
	};

	const renderLookupInput = function(ctx) {
		let _lookup = getLookup(ctx);
		if (isCombo === undefined) {
			isCombo = _lookup.supportsCombo;
		}
		let getText;
		if (customLookupTextCallback) {
			getText = function(value) {
				let res = customLookupTextCallback(ctx, value);
				return res || _lookup.getText(value);
			};
		} else {
			getText = _lookup.getText;
		}
		return <LookupInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			getRows={_lookup.getRows}
			getText={getText}
			timeout={timeout || _lookup.getTimeout()}
			combo={isCombo}
			mandatory={mandatory}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderLookupListInput = function(ctx) {
		let _lookup = getLookup(ctx);
		if (isCombo === undefined) {
			isCombo = _lookup.supportsCombo;
		}

		return <LookupListInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			getRows={_lookup.getRows}
			getText={_lookup.getText}
			timeout={timeout || _lookup.getTimeout()}
			combo={isCombo}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderDateInput = function(ctx) {
		return <DateInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			presets={presets}
			withTime={withTime}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderSearchDateInput = function(ctx) {
		return <SearchDateInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderSearchNumberInput = function(ctx) {
		return <SearchNumberInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	const renderSearchRuntimeInput = function(ctx) {
		return <SearchRuntimeInput
			value={getValue(ctx)}
			onChange={value => setValue(ctx, value)}
			readOnly={isReadOnly(ctx)}
			placeholder={getPlaceholder(ctx)}
		/>;
	};

	let isFocusable = true;

	this.isFocusable = function() {
		return isFocusable;
	};

	const updateRenderer = function() {
		isFocusable = true;
		if (!filterType) {
			filterType = type;
		}
		if (type === 'text') {
			renderInput = renderTextInput;
		} else if (type === 'lookup') {
			renderInput = renderLookupInput;
		} else if (type === 'lookupList') {
			renderInput = renderLookupListInput;
		} else if (type === 'date') {
			renderInput = renderDateInput;
			filterType = 'searchDate';
		} else if (type === 'searchDate') {
			renderInput = renderSearchDateInput;
		} else if (type === 'number') {
			renderInput = renderNumberInput;
			filterType = 'searchNumber';
		} else if (type === 'searchNumber') {
			renderInput = renderSearchNumberInput;
		} else if (type === 'searchRuntime') {
			renderInput = renderSearchRuntimeInput;
		} else if (type === 'runtime') {
			renderInput = renderRuntimeInput;
			filterType = 'searchRuntime';
		} else if (type === 'comment') {
			isFocusable = false;
			filterType = null;
			renderInput = renderCommentInput;
		} else if (type === 'entityLink') {
			isFocusable = false;
			filterType = null;
			renderInput = renderEntityLink;
		} else if (type === 'button') {
			isFocusable = false;
			filterType = null;
			renderInput = renderButton;
		} else if (type === 'password') {
			renderInput = renderPasswordInput;
			filterType = null;
		} else {
			isFocusable = false;
			renderInput = renderUnknown;
		}
	};

	updateRenderer();

	this.setProcessForSearch = function(value) {
		processForSearch = value;
		return this;
	};

	this.getProcessForSearch = function() {
		return processForSearch;
	};

	this.setName = function(value) {
		name = value;
		if (!path) {
			this.setPath(value);
		}
		return this;
	};

	this.setMultiline = function(value) {
		multiline = value;
		return this;
	};

	this.setInputMinHeight = function(value) {
		inputMinHeight = value;
		return this;
	}

	this.setIsCombo = function(value) {
		isCombo = !!value;
		return this;
	};

	this.setPath = function(value) {
		isRootPath = false;
		if (Array.isArray(value)) {
			if (value[0] === '/') {
				value = value.slice(1);
				isRootPath = true;
			}
		}
		path = value;
		return this;
	};

	this.setType = function(value) {
		type = value;
		updateRenderer();
		return this;
	};

	this.setLookup = function(value) {
		if (typeof value === 'function') {
			lookup = null;
			lookupCallback = value;
		} else {
			lookup = value;
			lookupCallback = null;
		}
		return this;
	};

	this.setIndexLookup = function(value) {
		if (typeof value === 'function') {
			indexLookup = null;
			indexLookupCallback = value;
		} else {
			indexLookup = value;
			indexLookupCallback = null;
		}
		return this;
	};

	this.getIndexLookup = function(ctx) {
		if (indexLookupCallback) {
			return indexLookupCallback(ctx);
		} else if (indexLookup) {
			if (typeof indexLookup === 'string') {
				if (indexLookup === '$value') {
					return null;
				}
				return ctx.getLookup(indexLookup);
			} else {
				return indexLookup;
			}
		}
		return this.getLookup(ctx);
	};

	this.setWidth = function(value) {
		width = value;
		return this;
	};

	this.setFixedWidth = function(value) {
		fixedWidth = value;
		width = value;
		return this;
	};

	this.getWidth = function() {
		return width;
	};

	this.getMinWidth = function() {
		return minWidth;
	};

	this.setValidator = function(value) {
		validator = value;
		return this;
	};



	const validate = function(errors, ctx) {
		if (!validator && !isMandatoryCallback && !mandatory) {
			return;
		}
		if (!isVisible(ctx)) {
			return;
		}
		const value = getValue(ctx);
		const fullPath = ctx.getFullPath(path, isRootPath);
		if (isMandatory(ctx) && isEmpty(value)) {
			errors.add(
				fullPath,
				{
					type: 'error',
					code: 'mandatoryField',
					longMessage: me.getLabel(ctx) + getPhrase('isAMandatoryField'),
					message: getPhrase('mandatoryField')
				}
			);
		} else {
			errors.clear(fullPath, 'mandatoryField');
		}
		if (validator) {
			if (validator.isValueValidator) {
				errors.add(ctx.getFullPath(path, isRootPath), validator.validate(value), 'invalidValue');
			} else if (validator.isContextValidator) {
				validator.validate(errors, ctx, name);
			}
		}
	};

	this.validateAll = function(ctx, errors) {
		if (noError) {
			return;
		}
		validate(errors, ctx);
	};

	this.validateOnChange = function(ctx, errors) {
		errors = errors || new ErrorList();
		validate(errors, ctx);
		if (this._parentDefinition && this._parentDefinition.validateOnChange) {
			this._parentDefinition.validateOnChange(ctx, errors, name);
		}
		return errors;
	};

	this.setReadOnly = function(value) {
		if (typeof value === 'function') {
			readOnly = false;
			isReadOnlyCallback = value;
		} else {
			readOnly = !!value;
			isReadOnlyCallback = null;
		}
		return this;
	};

	this.setMaxLength = function(value) {
		maxLength = value;
		return this;
	};

	this.setVisible = function(value) {
		if (typeof value === 'function') {
			isHidden = false;
			isVisibleCallback = value;
		} else {
			isHidden = !value;
			isVisibleCallback = null;
		}
		return this;
	};

	this.getLabel = function(ctx) {
		return label ?? name;
	};

	const isVisible = function(ctx) {
		if (isVisibleCallback) {
			return isVisibleCallback(ctx, name);
		} else {
			return !isHidden;
		}
	};

	this.render = function(ctx, key = 0) {
		if (!isVisible(ctx)) {
			return null;
		}
		if (inputOnly) {
			return renderInput(ctx);
		}
		return (
			<FormItem
				label={this.getLabel(ctx)}
				name={name}
				key={key}
				className={getClassName(ctx)}
				fixedWidth={fixedWidth}
				width={width}
				minWidth={minWidth}
				error={noError ? null : this.getError(ctx)}
				readOnly={isReadOnly(ctx)}
				hilited={this.isHilited(ctx)}
				leftIcons={getLeftIcons(ctx)}
				rightIcons={getRightIcons(ctx)}
				margin={margin}
			>
				{!getNoInput(ctx) && renderInput(ctx)}
			</FormItem>
		)
	};

	this.renderInput = function(ctx) {
		return renderInput(ctx);
	};

}

